<template>
  <b-card class="cardStyle">
    <p class="m-0 title">
      <b-icon icon="flag-fill" scale="1.3"></b-icon>&nbsp; &nbsp; Start-Up Status
    </p>
    <div v-if="loading" id="loading" class="text-center">
      <b-spinner class="align-middle" label="Spinning" type="grow"></b-spinner>
    </div>
    <b-row v-if="!loading">
      <b-col cols="12" lg="4" md="12" sm="12" xl="4">
        <milestone-map-chart
          :height="'21rem'"
          :milestoneMapChartData="this.milestoneMapChartData"
        ></milestone-map-chart>
        <b-card class="infoCard" no-body>
          <b-row class="m-0 pt-1 h-100" style="align-items: center">
            <b-col class="cardText text-center" cols="12" lg="12" md="6" sm="6" xl="6"
              ><strong>Global Sites Activated</strong>
              <div>(Activated/Selected)</div>
            </b-col>
            <b-col class="cardNumber" cols="12" lg="" md="6" sm="6" xl="6"
              >{{ sumActivated
              }}<span class="cardSumNumber"
                >/{{ sumSelected }} {{ percentageCalc(sumActivated, sumSelected) }}</span
              ></b-col
            >
          </b-row>
        </b-card>
        <b-card class="infoCard mt-2" no-body>
          <b-row class="m-0 pt-1 h-100" style="align-items: center">
            <b-col class="cardText text-center" cols="12" lg="12" md="6" sm="6" xl="6"
              ><strong>Global Sites with FSFV</strong>
              <div>(FSFV/Activated)</div>
            </b-col>
            <b-col class="cardNumber" cols="12" lg="" md="6" sm="6" xl="6"
              >{{ sumFSFV
              }}<span class="cardSumNumber"
                >/{{ sumActivated }} {{ percentageCalc(sumFSFV, sumActivated) }}</span
              ></b-col
            >
          </b-row>
        </b-card>
        <div class="redirectLink" @click="$bvModal.show('showCountryListModal')">
          Show country list
        </div>
      </b-col>
      <b-col cols="12" lg="8" md="12" sm="12" xl="8">
        <section class="mt-4">
          <p class="m-0 card-title">Start-Up Timeline <strong>Comparison</strong></p>
          <p class="card-subtitle mb-4 mt-3">
            A visualization of calendar days elapsed since site selection to your current milestone
            against country and study average for each milestone. All Pfizer study start-up
            requirements must be met regardless of where your site appears in comparison to the
            country and study average.
          </p>
          <div v-if="!showComparison" style="height:15rem;margin-top:2rem;">
            <div class="text-center custom-margin-y">
              <b-img :src="image" alt="Responsive image" fluid></b-img>
            </div>
            <p class="text-center mt-3" style="font-size:13px">
              Once milestones data become available you will be able to see a comparison of your
              site and the overall study progress
            </p>
          </div>
          <milestones-comparison-card
            v-if="showComparison"
            :chartData="chartData"
            :IIPSentToSite="IIPSentToSite"
            :height="'26rem'"
          ></milestones-comparison-card>
        </section>
      </b-col>
    </b-row>
    <b-modal
      id="showCountryListModal"
      body-class="pt-0"
      centered
      hide-footer
      size="lg"
      footerClass="p-2 border-top-0"
      headerClass="p-2 mt-2 border-bottom-0"
      @ok="$bvModal.hide('showCountryListModal')"
    >
      <b-row class="justify-content-center ">
        <b-col cols="10">
          <b-form-input
            id="input-1"
            v-model="filter"
            placeholder="Search by country name"
            type="search"
          ></b-form-input>
        </b-col>
      </b-row>
      <b-row class="justify-content-center mt-3">
        <b-col cols="10" class="">
          <b-table
            id="country-list"
            :sort-by.sync="sortBy"
            :filter="filter"
            :filter-included-fields="filterOn"
            :fields="fields"
            :items="countryList"
            bordered
            small
            class="rounded-lg text-center mt-3"
            empty-text="There are no country list data."
            fixed
            hover
            sticky-header
            responsive="sm"
            show-empty
            striped
            table-variant="light"
          ></b-table>
        </b-col>
      </b-row>
      <div class="text-right">
        *number of Sites
      </div>
      <template #modal-header="{ ok }">
        <p class="modal-header-text font-weight-bold pb-0 mb-0 pl-1 mx-auto">Country List</p>
        <b-button class="float-right" variant="link-dark" @click="ok()">
          <b-icon icon="x"></b-icon>
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
  import emptyDataImage from '../../../images/empty_data_icon.png';
  import MilestonesComparisonCard from './MilestonesComparisonCard';
  import MilestoneMapChart from './MilestoneMapChart';

  export default {
    components: {
      MilestoneMapChart,
      MilestonesComparisonCard,
    },
    props: {
      canRedirectToEnrollment: Boolean,
    },
    data() {
      return {
        filter: '',
        sortBy: 'name',
        sortDesc: true,
        filterOn: ['name'],
        perPage: 5,
        currentPage: 1,
        rows: 0,
        countryList: [],
        showComparison: false,
        loadingComparison: false,
        image: emptyDataImage,
        comparisonData: {
          Site: [],
          CountryAverages: [],
          StudyAverages: [],
        },
        loading: false,
        sumSelected: 0,
        sumActivated: 0,
        sumFSFV: 0,
        milestoneMapChartData: [],
        fields: [
          { key: 'name', label: 'Country', sortable: true },
          { key: 'selected', label: 'Selected*', sortable: true },
          { key: 'activated', label: 'Activated*', sortable: true },
          { key: 'activatedFSFV', label: 'With FSFV*', sortable: true },
        ],
        milestonesMap: {
          'Site Selection': ['Site Selection Date'],
          'IIP Sent to Site': ['IIP Sent to Site'],
          'IRB/IEC Submission': ['IRB Submission'],
          'IRB/IEC Approval': ['IRB/IEC Approval Site', 'Site IRB Approval Date'],
          'Clinical Study Agreement Fully Executed': ['CSA Fully Executed Date'],
          'Site Initiation': ['SIV', 'siv planned(scheduled / Siebel) date'],
          'Site Activation': ['Site Activated'],
          FSFV: ['Site FSFV', 'Site FSFV Date'],
        },
        seriesMap: {
          Site: {
            name: 'My Site',
            type: 'line',
            data: null,
            color: '#FFC043',
          },
          CountryAverages: {
            name: 'My Country Average',
            type: 'line',
            data: null,
            color: '#18A0FB',
          },
          StudyAverages: {
            name: 'Study Average',
            type: 'line',
            data: null,
            color: '#05A357',
          },
        },
        chartData: {
          definedSeries: [],
          definedData: [],
        },
        IIPSentToSite: [],
      };
    },
    computed: {
      isEnrollmentOversight() {
        return this.$route.name.includes('oversight');
      },
    },
    created() {
      this.initialize();
    },
    methods: {
      initialize() {
        this.loading = true;
        let studyId = this.$route.params['studyId'];
        let siteId = this.$route.params['siteId'];
        let endpoints = [
          `/api/get-study-start-up-interactive-world-map?studyId=${studyId}`,
          `/api/get-study-start-up-comparison-data?study=${studyId}&site=${siteId}`,
        ];
        Promise.allSettled(endpoints.map(endpoint => axios.get(endpoint)))
          .then(
            axios.spread((...allData) => {
              let [worldMapData, comparisonData] = allData;

              if (
                worldMapData.status === 'fulfilled' &&
                worldMapData.value.data['data'].length !== 0
              ) {
                this.setGlobalMilestonesData(worldMapData.value.data['data']);
              }
              if (comparisonData.status === 'fulfilled' && comparisonData.value.status !== 204) {
                this.setStartUpComparisonData(comparisonData.value.data['items']);
              }
              this.loading = false;
            }),
          )
          .catch(error => {
            console.error(error);
            this.loading = false;
          });
      },

      setGlobalMilestonesData(worldData) {
        this.milestoneMapChartData = worldData['countries'];
        Object.keys(this.milestoneMapChartData).forEach(el => {
          this.countryList.push({
            name: el,
            selected: this.milestoneMapChartData[el]['selected'],
            activatedFSFV: this.milestoneMapChartData[el]['activatedFSFV'],
            activated: this.milestoneMapChartData[el]['activated'],
          });
        });
        this.rows = this.countryList.length;
        this.sumSelected = worldData['total']['selected'];
        this.sumActivated = worldData['total']['activated'];
        this.sumFSFV = worldData['total']['activatedFSFV'];
      },
      setStartUpComparisonData(data) {
        this.showComparison = true;
        for (const dataItem of data) {
          dataItem['milestones'].forEach(milestone => {
            if (milestone['milestoneName'] === 'IIP Sent to Site') {
              this.IIPSentToSite[dataItem['type']] = milestone['dateElapsedFromStart'];
            }
            if (milestone['dateElapsedFromStart'] != null) {
              this.comparisonData[dataItem['type']].push(milestone['dateElapsedFromStart']);
            } else {
            }
          });
        }
        this.prepareChartConfig();
      },
      prepareChartConfig() {
        for (const item in this.comparisonData) {
          if (this.comparisonData[item].length > 0) {
            this.seriesMap[item]['data'] = this.comparisonData[item];
            this.chartData['definedSeries'].push(this.seriesMap[item]);
            this.chartData['definedData'].push(this.seriesMap[item]['name']);
          }
        }
      },
      percentageCalc(x, y) {
        return y !== 0 ? '(' + ((x / y) * 100).toFixed(1) + '%)' : '';
      },
      capitalize(s) {
        return s[0].toUpperCase() + s.slice(1);
      },
    },
  };
</script>

<style scoped>
  .title {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: #0060aa;
  }

  .infoCard {
    height: 4.3rem;
    border-radius: 12px;
    background: rgba(191, 191, 241, 0.12);
    border: 0;
    padding: 0px;
  }

  .cardText {
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0.25px;
    color: #6f6f6f;
  }

  .cardNumber {
    font-size: 20px;
    line-height: 30px;
    text-align: center;
    color: #01004e;
  }

  .cardSumNumber {
    color: #6f6f6f;
  }

  .redirectLink {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    letter-spacing: 0.25px;
    text-decoration-line: underline;
    color: #3c45ea;
    width: 100%;
    cursor: pointer;
    margin-top: 1rem;
  }

  .table-wrap {
    overflow-y: auto;
    height: 25rem;
  }
</style>
